import { useRef, useEffect, useMemo, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { Warp } from './Calendar.styled'
import { noop, get, map } from 'lodash'

const getDateKey = (date) => {
  const targetDate = date instanceof Date ? date : new Date(date)
  const year = targetDate.getFullYear()
  const month = String(targetDate.getMonth() + 1).padStart(2, '0')
  const day = String(targetDate.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const getStatusClassNames = (status, eventColorMode) => {
  if (eventColorMode === 'leaveType') return ['leave-type-event']

  if (status === 6) return ['leave-approved']
  if (status === 1) return ['leave-pending']
  if (status === 2) return ['leave-partial-approved']
  if (status === 3) return ['leave-pending-cancellation']
  if (status === 4) return ['leave-partial-cancellation']
  if (status === 5 || status === 7) return ['leave-reject']
  return []
}

const Calendar = ({
  date = new Date(),
  events = [],
  holidayEvents = [],
  weekendEvents = [],
  eventColorMode = 'status',
  showStatusIndicator = false,
  fillAvailable = false,
  eventClick = noop,
  select = noop
}) => {
  const calendarComponentRef = useRef()
  const wrapperRef = useRef()
  const [availableHeight, setAvailableHeight] = useState(undefined)

  useEffect(() => {
    if (!fillAvailable) {
      setAvailableHeight(undefined)
      return
    }

    let frame
    const updateSize = () => {
      window.cancelAnimationFrame(frame)
      frame = window.requestAnimationFrame(() => {
        const height = wrapperRef.current?.clientHeight
        const fitsDesktop = window.matchMedia('(min-width: 1200px)').matches

        setAvailableHeight(fitsDesktop && height > 0 ? height : undefined)
        calendarComponentRef.current?.getApi().updateSize()
      })
    }
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateSize)

    observer?.observe(wrapperRef.current)
    window.addEventListener('resize', updateSize)
    updateSize()

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', updateSize)
      window.cancelAnimationFrame(frame)
    }
  }, [fillAvailable])

  useEffect(() => {
    const calendarApi = calendarComponentRef.current.getApi()
    calendarApi.gotoDate(date)
  }, [date])

  const getEvents = useMemo(() => {
    return map(events, (itm) => ({
      ...itm
    }))
  }, [events])

  const getHolidayEvents = useMemo(() => {
    return holidayEvents
  }, [holidayEvents])

  const holidayTitleByDate = useMemo(() => {
    return holidayEvents.reduce((acc, item) => {
      const dateKey = getDateKey(item.start)
      const title = get(item, 'title', '')

      if (!title) return acc

      acc[dateKey] = acc[dateKey] ? `${acc[dateKey]}, ${title}` : title

      return acc
    }, {})
  }, [holidayEvents])

  const specificDates = useMemo(
    () => Object.keys(holidayTitleByDate),
    [holidayTitleByDate]
  )

  const handleDayCellClassNames = (info) => {
    const dateStr = getDateKey(info.date)

    return specificDates.includes(dateStr) ? ['holiday-cell'] : []
  }

  const handleDayCellDidMount = (info) => {
    const dateStr = getDateKey(info.date)
    const holidayTitle = holidayTitleByDate[dateStr]
    const dateNumber = info.el.querySelector('.fc-daygrid-day-number')

    if (!dateNumber) return

    if (holidayTitle) {
      dateNumber.setAttribute('data-holiday-title', holidayTitle)
    } else {
      dateNumber.removeAttribute('data-holiday-title')
    }
  }

  const getWeekendEvents = useMemo(() => {
    return weekendEvents
  }, [weekendEvents])

  return (
    <Warp ref={wrapperRef} fillAvailable={fillAvailable}>
      <FullCalendar
        ref={calendarComponentRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        height={availableHeight}
        headerToolbar={
          availableHeight
            ? false
            : {
                left: '',
                center: '',
                right: ''
              }
        }
        initialView="dayGridMonth"
        dayCellClassNames={handleDayCellClassNames}
        dayCellDidMount={handleDayCellDidMount}
        selectable={true}
        dragScroll={false}
        selectMirror={true}
        dayMaxEvents={true}
        displayEventEnd={showStatusIndicator}
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        }}
        weekends={true}
        eventSources={[
          {
            events: getEvents
          },
          {
            events: getHolidayEvents,
            color: 'rgba(54, 215, 121, 0)',
            textColor: '#000',
            editable: false
          },
          {
            events: getWeekendEvents,
            color: '#DD561C',
            editable: false
          }
        ]}
        eventClassNames={(eventInfo) =>
          getStatusClassNames(
            eventInfo.event._def.extendedProps.status,
            eventColorMode
          )
        }
        select={select}
        eventContent={(eventInfo) =>
          renderEventContent(eventInfo, showStatusIndicator)
        }
        eventClick={eventClick}
      />
    </Warp>
  )
}

function renderEventContent(eventInfo, showStatusIndicator) {
  const title = get(eventInfo, 'event._def.title')

  if (!showStatusIndicator) return title

  const timeText = get(eventInfo, 'timeText', '')
  const statusLabel = get(eventInfo, 'event._def.extendedProps.statusLabel', '')
  const indicator = get(
    eventInfo,
    'event._def.extendedProps.statusIndicator',
    ''
  )
  const statusColor = get(eventInfo, 'event._def.extendedProps.statusColor', '')
  const statusTextColor = get(
    eventInfo,
    'event._def.extendedProps.statusTextColor',
    '#FFFFFF'
  )

  return (
    <span className="leave-event-content">
      {indicator && (
        <span
          aria-label={statusLabel}
          className="leave-event-status-indicator"
          style={{
            backgroundColor: statusColor,
            color: statusTextColor
          }}
          title={statusLabel}
        >
          {indicator}
        </span>
      )}
      {timeText && <span className="leave-event-time">{timeText}</span>}
      <span className="leave-event-title">{title}</span>
    </span>
  )
}

export default Calendar
